import { Button, Dialog, DialogActions, DialogContent, Stack } from '@mui/material';
import { ErrorMessage, Form, Formik, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import React from 'react';

import { useAddOrgToAssessment, useGetOrganizations } from '@/api';
import { SelectFormField, UIDialogTitle } from '@/shared/components';
import { useMedia } from '@/shared/hooks';
import { useAssessmentsContext } from '@/shared/providers';

import { AdminToolsFormValues, validationSchema } from './duck';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const AssessmentAdminModal: React.FC<Props> = ({
  isOpen,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isSmallScreen } = useMedia();
  const { addOrgToAssessment } = useAddOrgToAssessment();
  const { selectedAssessment, setSelectedAssessment } = useAssessmentsContext();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const {
    organizations,
    isOrganizationsLoading,
    isOrganizationsError,
    refetchOrganizations,
  } = useGetOrganizations({
    // TODO: ability to get full list
    sortBy: 'name',
    sortOrder: 'asc',
  });

  const initialFormValues: AdminToolsFormValues = React.useMemo(() => {
    return {
      organizationId: selectedAssessment?.organizationId || '',
      verticalId: '',
    };
  }, [selectedAssessment]);

  const handleSubmit = async (values: AdminToolsFormValues, { resetForm }: FormikHelpers<AdminToolsFormValues>) => {
    setIsSubmitting(true);

    try {
      if (!selectedAssessment?.id) {
        enqueueSnackbar({
          message: 'Assessment ID is missing',
          variant: 'error',
        });

        return;
      }

      await addOrgToAssessment({
        assessmentId: selectedAssessment.id,
        organization_id: values.organizationId
      });

      enqueueSnackbar({
        message: 'Assessment updated successfully!',
        variant: 'success',
      });

      resetForm();
      handleClose();
    } catch (error) {
      enqueueSnackbar({
        message: `An unexpected error occurred${error instanceof Error ? ': ' + error.message : ''}`,
        variant: 'error',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedAssessment(undefined);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullScreen={isSmallScreen}
      fullWidth
      maxWidth='sm'
      scroll='paper'
      aria-labelledby='dialog-title'
      aria-describedby='dialog-content'
    >
      <UIDialogTitle id='dialog-title' title={'Admin Tools'} onCloseBtnAction={handleClose} />

      <Formik<AdminToolsFormValues>
        initialValues={initialFormValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        validateOnChange
        enableReinitialize
      >
        {({ isSubmitting: formikIsSubmitting, errors, touched, resetForm }) => {
          const isFormDisabled = isSubmitting || formikIsSubmitting;

          return (
            <Form>
              <DialogContent id='dialog-content'>
                <Stack
                  gap='2rem'
                  flexDirection='column'
                  width='100%'
                >
                  <SelectFormField
                    required
                    name='organizationId'
                    options={organizations}
                    fullWidth
                    label='Assign organization to assessment'
                    variant='outlined'
                    helperText={
                      touched.organizationId && errors.organizationId ? (
                        <ErrorMessage name='organizationId' />
                      ) : isOrganizationsError ? (
                        'Failed to load organizations'
                      ) : ''
                    }
                    disabled={isOrganizationsError || isFormDisabled}
                    error={Boolean(touched.organizationId && errors.organizationId) || isFormDisabled}
                    isLoading={isOrganizationsLoading}
                    isErrorIcon={isOrganizationsError}
                    onErrorIconAction={refetchOrganizations}
                  />
                </Stack>
              </DialogContent>

              <DialogActions>
                <Button
                  variant='outlined'
                  color='secondary'
                  onClick={() => {
                    resetForm();
                    handleClose();
                  }}
                  disabled={isFormDisabled}
                >
                  Cancel
                </Button>
                <Button
                  variant='contained'
                  type='submit'
                  disabled={isFormDisabled}
                >
                  Save
                </Button>
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default AssessmentAdminModal;